import './style.scss'
import styled, { css, StyledComponent } from 'styled-components'


export const tokens = {
    primary: '#0D968F',
    helplink: '#0A756F',
    primaryHover: '#0B837D',
    primarySoft: '#E7F6F5',
    primaryDark: '#064542',
    success: '#16A34A',
    successHover: '#128A52',
    successSoft: '#ECFDF3',
    successText: '#027A48',
    textDark: '#101828',
    textMuted: '#667085',
    textLabel: '#A098AE',
    textValue: '#58595B',
    border: '#EAECF0',
    borderSoft: '#F7FAFC',
    pageBg: '#F9FAFB',
    white: '#ffffff',
    shadowCard: '0px 1px 2px rgba(16, 24, 40, 0.05)',
    shadowPopup:
        '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
}

/** Shared laptop -> mobile breakpoints (max-width, px) for componentv2. */
export const breakpoints = {
    laptop: 1024,
    tablet: 768,
    mobile: 480,
}

/** styled-components media-query helpers, e.g. `${media.tablet}{ ... }` */
export const media = {
    laptop: `@media (max-width: ${breakpoints.laptop}px)`,
    tablet: `@media (max-width: ${breakpoints.tablet}px)`,
    mobile: `@media (max-width: ${breakpoints.mobile}px)`,
}

export type Variant = 'primary' | 'success' | 'outline' | 'ghost'
export type Size = 'sm' | 'md' | 'lg'
export type IconPosition = 'left' | 'right'

export interface UIProps {
    width?: string
    height?: string
    minHeight?: string
    maxWidth?: string
    padding?: string
    paddingX?: string
    paddingY?: string
    margin?: string
    topMargin?: string
    bottomMargin?: string
    bgColor?: string
    hoverBgColor?: string
    color?: string
    borderColor?: string
    radius?: string
    shadow?: string
    fontSize?: string
    fontWeight?: string
    align?: string
    gap?: string
    justify?: string
    items?: string
    direction?: 'row' | 'column'
    wrap?: string
    cursor?: string
    fluidOn?: number
    className?: string
}

/** number -> rem, while leaving %, px, rem, and keywords untouched */
export const rem = (v?: string) =>
    v == null ? undefined : /[%a-z]/i.test(v) ? v : `${v}rem`


export const Box: StyledComponent<'div', any, UIProps, never> = styled.div<UIProps>`
    box-sizing: border-box;
    width: ${({ width }) => rem(width) ?? 'auto'};
    height: ${({ height }) => rem(height) ?? 'auto'};
    min-height: ${({ minHeight }) => rem(minHeight) ?? 'auto'};
    max-width: ${({ maxWidth }) => rem(maxWidth) ?? 'none'};
    background-color: ${({ bgColor }) => bgColor ?? 'transparent'};
    border: ${({ borderColor }) =>
            borderColor ? `1px solid ${borderColor}` : 'none'};
    border-radius: ${({ radius }) => rem(radius) ?? '0'};
    box-shadow: ${({ shadow }) => shadow ?? 'none'};
    padding: ${({ padding, paddingX, paddingY }) =>
            padding
                    ? rem(padding)
                    : `${rem(paddingY) ?? '0'} ${rem(paddingX) ?? '0'}`};
    margin: ${({ margin }) => rem(margin) ?? '0'};
    margin-top: ${({ topMargin }) => rem(topMargin) ?? '0'};
    margin-bottom: ${({ bottomMargin }) => rem(bottomMargin) ?? '0'};
    cursor: ${({ cursor }) => cursor ?? 'default'};
    transition: all 0.2s ease;
    ${({ hoverBgColor }) =>
            hoverBgColor && css`&:hover { background-color: ${hoverBgColor}; }`}
    ${({ fluidOn }) =>
            fluidOn &&
            css`
                @media (max-width: ${fluidOn}px) {
                    width: 100%;
                    max-width: 100%;
                }
            `}
`
export const Flex: StyledComponent<'div', any, UIProps, never> = styled(Box)<UIProps>`
    display: flex;
    flex-direction: ${({ direction }) => direction ?? 'row'};
    align-items: ${({ items }) => items ?? 'center'};
    justify-content: ${({ justify }) => justify ?? 'flex-start'};
    gap: ${({ gap }) => rem(gap) ?? '0'};
    flex-wrap: ${({ wrap }) => wrap ?? 'nowrap'};
`
export const Text: StyledComponent<'span', any, UIProps, never> = styled.span<UIProps>`
    display: inline-block;
    color: ${({ color }) => color ?? tokens.textDark};
    font-size: ${({ fontSize }) => rem(fontSize) ?? '1.4rem'};
    font-weight: ${({ fontWeight }) => fontWeight ?? '400'};
    text-align: ${({ align }) => align ?? 'left'};
    line-height: 1.45;
    margin: ${({ margin }) => rem(margin) ?? '0'};
    margin-top: ${({ topMargin }) => rem(topMargin) ?? '0'};
    margin-bottom: ${({ bottomMargin }) => rem(bottomMargin) ?? '0'};
`